
import { Button, Toggle } from '05-ui-kit';
import { computed, inject, onBeforeUnmount, onMounted, onUnmounted, onUpdated } from '@vue/composition-api';
import scrollIntoView from 'smooth-scroll-into-view-if-needed';
import { defineComponent, useContext, useMeta, useRoute } from '@nuxtjs/composition-api';
import { storeToRefs } from 'pinia';
import InfoBlockInstallment from '../InfoBlocks/InfoBlockInstallment/InfoBlockInstallment.vue';
import { useSocialShareStore } from '~/store/modal/socialShare';
import getOpenGraphMarkup from '~/helper/getOpenGraphMarkup';
import type { EmitteryType } from '~/plugins/emitter';
import UIActionEntity from '~/data/UIActionEntity/UIActionEntity.vue';
import ProductPriceBlock from '~/components/ProductPrice/ProductPriceBlock.vue';
import ActionCart from '~/action/ActionCart.vue';
import AddToCart from '~/components/AddToCart/AddToCart.vue';
import QuickOrderToggle from '~/components/product/QuickOrderToggle/QuickOrderToggle.vue';
import FoundCheaper from '~/components/FoundCheaper/FoundCheaper.vue';
import UserTools from '~/components/UserTools/UserTools.vue';
import Breadcrumb from '~/components/Breadcrumb/Breadcrumb.vue';
import { useLocationStore } from '~/store/location';
import { useCartStore } from '~/store/cart';
import { useResizeStore } from '~/store/resize';
import { useUserStore } from '~/store/user';
import LazyLoan from '~/components/Loan/Loan.vue';
import DeliveryType from '~/components/DeliveryType/DeliveryType.vue';
type ResponseTypeGetByID = Awaited<ReturnType<typeof context.$api.product.getByID>>['result'];
interface ResponseTypeBuyOneClick {
  [key: string]: any;
}
interface Sticky {
  destroy: () => void;
}
const __sfc_main = defineComponent({
  head: {}
});
__sfc_main.props = {
  response: {
    key: "response",
    required: true,
    type: null
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;
  const context = useContext();
  const socialShareStore = useSocialShareStore();
  const route = useRoute();
  const emitter = inject<EmitteryType>('emitter')!;
  const {
    currentCity
  } = storeToRefs(useLocationStore());
  const {
    data: cartData
  } = storeToRefs(useCartStore());
  const resizeStore = useResizeStore();
  const {
    user: userData,
    isUser
  } = useUserStore();
  const cartList = computed(() => cartData.value);
  const cartCount = computed(() => cartList.value?.[props.response.product.id]?.quantity || 0);
  const price = computed(() => props.response.product.price?.common?.value);
  const isPreOrder = computed(() => props.response.product.status?.code === 'PRE_ORDER');
  /**
   * Проверяем если пользователь находится на подстаницах продукта, то делаем последнюю хлебную крошку ссылкой.
   */
  const isSubPage = computed(() => {
    const subPageList = ['question', 'review', 'property', 'accessory'];
    return subPageList.some(el => route.value.path.includes(el));
  });
  const tabs = computed(() => {
    return [{
      link: 'property/',
      name: 'Характеристики'
    }, {
      link: 'review/',
      name: 'Отзывы',
      count: props.response.product.rate?.reviewCount
    }, {
      link: 'question/',
      name: 'Вопросы и ответы'
    }, ...(props.response.product.accessory.length ? [{
      link: 'accessory/',
      name: 'Аксессуары'
    }] : [])];
  });
  let unsubscribe: () => void;
  let stickySidebar: Sticky | null = null;
  onUpdated(() => {
    if (!route.value.fullPath.includes('accessory')) scrollToTop();
  });
  onMounted(() => {
    require('sticky-sidebar-v2/dist/sticky-sidebar');
    if (!resizeStore.isMobile) initStickySidebar();
    scrollToTop();
    unsubscribe = resizeStore.$onAction(mutation => {
      if (mutation.name === 'setIsMobile') {
        if (mutation.store.isMobile && stickySidebar) {
          stickySidebar?.destroy();
          stickySidebar = null;
        } else {
          initStickySidebar();
        }
      }
    });
    emitter.emit('product-view', {
      product: {
        ...props.response.product,
        id: Number(props.response.product.id),
        name: props.response.product.name,
        price: props.response.product.price,
        brand: props.response.product.brand,
        category: props.response.category,
        offer: props.response.offer,
        quantity: 1
      },
      statusCode: props.response.product.status?.code ?? ''
    });
  });
  onBeforeUnmount(() => {
    stickySidebar?.destroy();
  });
  onUnmounted(() => {
    if (unsubscribe) unsubscribe();
  });
  function onClickBuyCredit() {
    const ORDER_NUMBER = `TI-${Math.floor(Math.random() * 999999) + 1}|${props.response.product.id}|${userData?.id ? userData?.id : 'guest'}|${currentCity?.value?.id}`;
    tinkoff.create({
      orderNumber: ORDER_NUMBER,
      shopId: context.env.TINKOFF_SHOP_ID,
      showcaseId: context.env.TINKOFF_SHOWCASE_ID,
      items: [{
        name: props.response.product.name,
        price: price.value,
        quantity: 1
      }],
      sum: price.value
    });
    emitter.emit('loan-form-open');
  }
  function onSuccessQuickOrder({
    response
  }: {
    response: ResponseTypeBuyOneClick;
  }) {
    emitter.emit('after-checkout', {
      userId: userData?.id,
      order: {
        id: response.result?.ORDER_ID,
        product: [{
          ...props.response?.product,
          offer: props.response?.offer
        }],
        bonus: {
          income: props.response?.product?.price?.bonus
        }
      }
    });
  }
  function initStickySidebar() {
    window.requestAnimationFrame(() => {
      stickySidebar = new StickySidebar('.product-page__sidebar-wrapper', {
        topSpacing: 20,
        bottomSpacing: 0,
        resizeSensor: true,
        minWidth: 800,
        containerSelector: '.product-page__sidebar',
        innerWrapperSelector: '.product-page__sidebar-content'
      });
    });
  }
  function scrollToTop() {
    scrollIntoView(document.querySelector('.site-carcass')!, {
      block: 'start',
      behavior: 'auto'
    });
  }
  function scrollNavigationToCenter() {
    scrollIntoView(document.querySelector('.product-page__nav-link_active')!, {
      behavior: 'smooth',
      inline: 'center'
    });
  }
  useMeta(() => ({
    title: props.response.meta?.element?.title,
    meta: [{
      hid: 'description',
      name: 'description',
      content: props.response.meta?.element?.description
    }, {
      hid: 'keywords',
      name: 'keywords',
      content: props.response.meta?.element?.keywords
    }],
    ...getOpenGraphMarkup({
      type: 'product',
      url: context.env.projectURL + route.value.fullPath,
      title: props.response.meta?.element?.title,
      description: props.response.meta?.element?.description,
      image: props.response.product?.image || context.env.projectURL + require('~/assets/images/05-avatar.png'),
      priceAmount: props.response.product?.price?.common.value
    }),
    ...(props.response.product?.status?.code === 'NOT_AVAILABLE' ? [{
      name: 'robots',
      content: 'noindex'
    }] : []),
    script: [{
      src: '/vue/onlineScript.js',
      async: true
    }]
  }));
  const getTitle = computed(() => {
    if (!props.response.product.name) return undefined;
    switch (route.value.name) {
      case 'product-properties':
        return `Характеристики: ${props.response.product.name}`;
      case 'product-review-list':
        return `Отзывы: ${props.response.product.name}`;
      case 'product-question-list':
        return `Вопросы и ответы: ${props.response.product.name}`;
      case 'product-accessories':
        return `Аксессуары: ${props.response.product.name}`;
      default:
        return props.response.product.name;
    }
  });
  return {
    props,
    socialShareStore,
    emitter,
    isUser,
    cartCount,
    price,
    isPreOrder,
    isSubPage,
    tabs,
    onClickBuyCredit,
    onSuccessQuickOrder,
    scrollNavigationToCenter,
    getTitle
  };
};
__sfc_main.components = Object.assign({
  Breadcrumb,
  UIActionEntity,
  UserTools,
  ProductPriceBlock,
  ActionCart,
  AddToCart,
  LazyLoan,
  InfoBlockInstallment,
  Button,
  QuickOrderToggle,
  DeliveryType,
  Toggle,
  FoundCheaper
}, __sfc_main.components);
export default __sfc_main;
